import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

// Session storage keys — exported so CustomerApp.js can read ops session
export const SESSION_KEY_AUTH      = 'basicAuthAuthenticated';
export const SESSION_KEY_USER      = 'basicAuthUser';
export const SESSION_KEY_OPS_TOKEN = 'opsAccessToken';
export const SESSION_KEY_OPS_DATA  = 'opsCustomerData';
export const SESSION_KEY_BRAND     = 'gatewayBrand';   // NAWABI | BAWARCHI | PARADISE

// All valid brand-ops session user keys
const BRAND_OPS_USERS = ['biryani_operations', 'biryani_bawarchi', 'biryani_paradise'];

// Resolve the API base URL (no /api suffix)
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://biryani.com';

async function callGatewayLogin(username, password) {
  try {
    const res = await fetch(`${API_BASE}/api/customer/gateway/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const json = await res.json();
    if (!res.ok) return { success: false, error: json.error || 'Login failed' };
    return json;
  } catch (e) {
    return { success: false, error: 'Network error — please try again.' };
  }
}

const BasicAuth = ({ children }) => {
  const location = useLocation();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [redirectHome, setRedirectHome]       = useState(false);
  const [username, setUsername]               = useState('');
  const [password, setPassword]               = useState('');
  const [showPw, setShowPw]                   = useState(false);
  const [error, setError]                     = useState('');
  const [loading, setLoading]                 = useState(false);

  // ── Restore existing session ─────────────────────────────────────────────
  useEffect(() => {
    const authOk   = sessionStorage.getItem(SESSION_KEY_AUTH) === 'true';
    const authUser = sessionStorage.getItem(SESSION_KEY_USER);
    if (authOk && (authUser === 'biryani_customer' || BRAND_OPS_USERS.includes(authUser))) {
      setIsAuthenticated(true);
      return;
    }
    // URL param shortcut: ?username=biryani_operations (legacy support)
    const params = new URLSearchParams(location.search);
    if (params.get('username') === 'biryani_operations') {
      triggerOpsAutoLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Legacy auto-login shortcut for Biryani Operations
  const triggerOpsAutoLogin = useCallback(async () => {
    setLoading(true);
    const gwUser = process.env.REACT_APP_GW_OPS_USER || 'biryani_operations';
    const gwPass = process.env.REACT_APP_GW_OPS_PASS || 'Biryani@1801';
    const res = await callGatewayLogin(gwUser, gwPass);
    setLoading(false);
    if (res.success && res.data?.account_type === 'operations') {
      _storeOpsSession(gwUser.toLowerCase().replace(/\s+/g, '_'), res.data);
      setIsAuthenticated(true);
    }
  }, []);

  // ── Store ops session helper ─────────────────────────────────────────────
  function _storeOpsSession(userKey, data) {
    sessionStorage.setItem(SESSION_KEY_AUTH,      'true');
    sessionStorage.setItem(SESSION_KEY_USER,      userKey);
    sessionStorage.setItem(SESSION_KEY_BRAND,     data.brand || 'NAWABI');
    sessionStorage.setItem(SESSION_KEY_OPS_TOKEN, data.access_token || '');
    sessionStorage.setItem(SESSION_KEY_OPS_DATA,  JSON.stringify(data.customer || {}));
  }

  // ── Form submit ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const res = await callGatewayLogin(username.trim(), password);
    setLoading(false);

    if (!res.success) {
      setError(res.error || 'Invalid username or password.');
      setPassword('');
      return;
    }

    const { account_type, brand, access_token, customer } = res.data || {};

    if (account_type === 'operations') {
      // Derive a stable session key from the username
      const userKey = username.trim().toLowerCase().replace(/\s+/g, '_');
      _storeOpsSession(userKey, { brand, access_token, customer });
      setIsAuthenticated(true);
    } else if (account_type === 'customer') {
      sessionStorage.setItem(SESSION_KEY_AUTH, 'true');
      sessionStorage.setItem(SESSION_KEY_USER, 'biryani_customer');
      sessionStorage.removeItem(SESSION_KEY_OPS_TOKEN);
      sessionStorage.removeItem(SESSION_KEY_OPS_DATA);
      sessionStorage.removeItem(SESSION_KEY_BRAND);
      setIsAuthenticated(true);
    } else {
      setError('Unexpected response. Please contact support.');
    }
  };

  if (redirectHome)                   return <Navigate to="/" replace />;
  if (loading && !isAuthenticated)    return <LoadingScreen />;
  if (isAuthenticated)                return <>{children}</>;

  return (
    <div style={S.container}>
      <div style={S.box}>
        <div style={S.logoWrap}>
          <img
            src="/biryani-logo.png"
            alt="Biryani"
            style={{ height: 60, width: 'auto', objectFit: 'contain' }}
            onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'block'; }}
          />
          <span style={{ display: 'none', fontSize: '3rem' }}>🍛</span>
        </div>
        <h2 style={S.title}>Customer Login</h2>
        <p style={S.sub}>Enter your credentials to access the customer portal</p>

        <form onSubmit={handleSubmit} style={S.form}>
          <div style={S.group}>
            <label style={S.label}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={S.input}
              placeholder="Enter username"
              autoComplete="username"
              autoFocus
              required
            />
          </div>

          <div style={S.group}>
            <label style={S.label}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...S.input, paddingRight: '3rem' }}
                placeholder="Enter password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                style={S.eyeBtn}
                tabIndex={-1}
              >
                {showPw ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {error && <div style={S.error}>{error}</div>}

          <button type="submit" style={{ ...S.btn, opacity: loading ? 0.7 : 1 }} disabled={loading}>
            {loading ? 'Authenticating…' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

const LoadingScreen = () => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0f0600 0%, #1f0f05 100%)', gap: 16 }}>
    <div style={{ fontSize: '3rem' }}>🍛</div>
    <p style={{ color: '#ffcc99', fontSize: '1rem', margin: 0 }}>Authenticating…</p>
  </div>
);

const S = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0f0600 0%, #1f0f05 100%)', padding: '20px' },
  box: { background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderRadius: '20px', border: '1px solid rgba(255,165,75,0.2)', boxShadow: '0 0 60px rgba(255,140,0,0.15)', padding: '2.75rem 2.5rem', maxWidth: '440px', width: '100%' },
  logoWrap: { display: 'flex', justifyContent: 'center', marginBottom: '1rem' },
  title: { fontFamily: '"Cormorant Garamond","Georgia",serif', fontSize: '2rem', fontWeight: 600, color: '#ffcc99', textAlign: 'center', margin: '0 0 0.4rem', letterSpacing: '0.05em' },
  sub: { fontSize: '0.9rem', color: 'rgba(255,217,179,0.65)', textAlign: 'center', marginBottom: '2rem', lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: '1.2rem' },
  group: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontSize: '0.85rem', color: '#ffd9b3', fontWeight: 500, letterSpacing: '0.04em' },
  input: { padding: '0.75rem 1rem', fontSize: '1rem', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,165,75,0.3)', borderRadius: '10px', color: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box' },
  eyeBtn: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', padding: '4px', lineHeight: 1 },
  btn: { padding: '0.875rem 1.5rem', fontSize: '1rem', fontWeight: 700, background: 'linear-gradient(135deg, #ff9933 0%, #ff7700 100%)', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: '0.75rem', boxShadow: '0 4px 16px rgba(255,119,0,0.3)' },
  error: { padding: '0.7rem 1rem', background: 'rgba(255,59,48,0.12)', border: '1px solid rgba(255,59,48,0.35)', borderRadius: '8px', color: '#ff7070', fontSize: '0.9rem', textAlign: 'center' },
};

export default BasicAuth;
